/**
 * Memory-based cache adapter using Map
 *
 * This adapter wraps the TtlCache implementation and provides the CacheAdapter
 * interface. It is the default cache used when no custom adapter is provided.
 */

import { TtlCache, CacheOptions } from '../cache';
import { CacheAdapter, CacheAdapterWithStats, CacheStats } from '../cache-interface';

/**
 * In-memory cache adapter using Map with TTL support
 *
 * This is the default cache implementation that wraps the existing TtlCache.
 * All methods return Promises for consistency with the async CacheAdapter interface,
 * even though operations are synchronous.
 *
 * Features:
 * - TTL-based expiration
 * - LRU-style eviction when max size is reached
 * - Automatic expiration checking on access
 * - Manual cache management (clear, prune, stats)
 *
 * @template T - Type of values stored in the cache
 *
 * @example
 * ```typescript
 * const cache = new MemoryCacheAdapter<MyData>({
 *   ttl: 15 * 60 * 1000,  // 15 minutes
 *   maxSize: 1000,
 *   enabled: true
 * });
 *
 * await cache.set('key1', myData);
 * const value = await cache.get('key1');
 * ```
 */
export class MemoryCacheAdapter<T> implements CacheAdapterWithStats<T> {
  private readonly cache: TtlCache<T>;
  private readonly enabled: boolean;

  /**
   * Creates a new memory cache adapter
   *
   * @param options - Cache configuration options
   */
  constructor(options: CacheOptions = {}) {
    this.cache = new TtlCache<T>(options);
    this.enabled = options.enabled ?? true;
  }

  /**
   * Retrieves a value from the cache
   *
   * @param key - Cache key
   * @returns Cached value or undefined if not found or expired
   */
  async get(key: string): Promise<T | undefined> {
    if (!this.enabled) {
      return undefined;
    }
    return this.cache.get(key);
  }

  /**
   * Stores a value in the cache
   *
   * Note: The current TtlCache implementation doesn't support per-key TTL.
   * The ttl parameter is accepted for interface compatibility but uses
   * the cache-wide TTL configured in the constructor.
   *
   * @param key - Cache key
   * @param value - Value to cache
   * @param ttl - Time-to-live in seconds (currently ignored, uses cache-wide TTL)
   */
  async set(key: string, value: T, ttl?: number): Promise<void> {
    if (!this.enabled) {
      return;
    }
    // Note: Current TtlCache doesn't support per-key TTL
    // Future enhancement: extend TtlCache to support per-key TTL
    this.cache.set(key, value);
  }

  /**
   * Removes a specific entry from the cache
   *
   * @param key - Cache key to delete
   * @returns true if key was deleted, false if it didn't exist
   */
  async delete(key: string): Promise<boolean> {
    return this.cache.delete(key);
  }

  /**
   * Clears all entries from the cache
   */
  async clear(): Promise<void> {
    this.cache.clear();
  }

  /**
   * Checks if a key exists in the cache and is not expired
   *
   * @param key - Cache key
   * @returns true if key exists and is valid
   */
  async has(key: string): Promise<boolean> {
    if (!this.enabled) {
      return false;
    }
    return this.cache.has(key);
  }

  /**
   * Gets cache statistics
   *
   * @returns Cache statistics including size, TTL, and configuration
   */
  async getStats(): Promise<CacheStats> {
    return this.cache.getStats();
  }

  /**
   * Removes expired entries from the cache
   *
   * This method is useful for freeing up memory by removing stale entries.
   * The cache automatically checks expiration on access, but this method
   * allows for proactive cleanup.
   *
   * @returns Number of entries removed
   */
  async prune(): Promise<number> {
    return this.cache.prune();
  }
}
